import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Star, Zap, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

// ============================================
// SUBSCRIPTION PAGE - Forbes Style
// ============================================
export const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('annual');

  const plans = [
    {
      id: 'monthly',
      name: 'Mensual',
      price: 9.99,
      period: '/mes',
      features: [
        'Acceso a todas las noticias',
        'Edición semanal completa',
        'Índice de Influencia',
        'Sin anuncios',
      ],
    },
    {
      id: 'annual',
      name: 'Anual',
      price: 79.99,
      period: '/año',
      discount: 'Ahorra 33%',
      popular: true,
      features: [
        'Todo lo del plan mensual',
        'Contenido exclusivo',
        'Alertas personalizadas',
        'Acceso anticipado',
        'Descarga de PDFs',
      ],
    },
  ];

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    // TODO: Implement Stripe checkout
    alert('Checkout con Stripe - Próximamente');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Crown className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: SERIF_FONT }}>
            Hazte Premium
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Accede a análisis exclusivos, contenido sin límites y herramientas para mantenerte informado.
          </p>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative rounded-2xl p-8 cursor-pointer transition-all ${
                selectedPlan === plan.id
                  ? 'border-2 border-red-600 shadow-xl'
                  : 'border-2 border-gray-200 hover:border-gray-300'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MÁS POPULAR
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                {plan.discount && (
                  <span className="inline-block mt-2 text-sm text-green-600 font-medium">
                    {plan.discount}
                  </span>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={handleSubscribe}
                className={`w-full h-12 ${
                  selectedPlan === plan.id
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-900 hover:bg-gray-800'
                }`}
              >
                {selectedPlan === plan.id ? 'Suscribirse' : 'Seleccionar'}
              </Button>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: SERIF_FONT }}>
            ¿Por qué Premium?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Star,
                title: 'Contenido Exclusivo',
                desc: 'Análisis profundos y reportajes que no encontrarás en ningún otro lugar.',
              },
              {
                icon: Zap,
                title: 'Acceso Anticipado',
                desc: 'Lee las noticias más importantes antes que nadie.',
              },
              {
                icon: Crown,
                title: 'Sin Límites',
                desc: 'Accede a todo el archivo y descarga ediciones en PDF.',
              },
            ].map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SUBSCRIPTION SUCCESS PAGE
// ============================================
export const SubscriptionSuccessPage = () => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: SERIF_FONT }}>
          ¡Bienvenido a Premium!
        </h1>
        <p className="text-gray-600 mb-8">
          Tu suscripción ha sido activada. Ahora tienes acceso completo a todo el contenido exclusivo.
        </p>
        <Link to="/">
          <Button className="bg-red-600 hover:bg-red-700">
            Explorar Contenido
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default SubscriptionPage;
